import type { JSX } from 'react';
import type { SurfaceOf } from './types';
import { flagClass } from './format';
import { useT } from '../i18n/useT';

type Table = SurfaceOf<'table'>;

function cellText(v: string | number | boolean | null): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  return String(v);
}

const FLAG_KEYS = ['flag', 'flags', 'abnormalFlag', 'interpretation'];

export function TableSurfaceView({ surface }: { surface: Table }): JSX.Element {
  const t = useT();
  const flagKey = surface.columns.find((c) => FLAG_KEYS.includes(c.key))?.key ?? FLAG_KEYS.find((k) => surface.rows.some((r) => k in r));
  return (
    <section className="surface surface-table">
      {surface.title ? <h3 className="surface-title">{surface.title}</h3> : null}
      {surface.rows.length === 0 ? (
        <p className="muted">{surface.emptyMessage ?? t('none')}</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                {surface.columns.map((c) => (
                  <th key={c.key} style={{ textAlign: c.align ?? 'left' }} scope="col">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {surface.rows.map((row, ri) => {
                const flag = flagKey ? row[flagKey] : undefined;
                const rowClass = typeof flag === 'string' ? flagClass(flag).replace('flag ', 'row-') : '';
                return (
                  <tr key={ri} className={rowClass}>
                    {surface.columns.map((c) => {
                      const v = row[c.key] ?? null;
                      const isFlag = c.key === flagKey && typeof v === 'string' && v.length > 0;
                      return (
                        <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                          {isFlag ? <span className={flagClass(v)}>{v}</span> : cellText(v)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {surface.sourceRefs && surface.sourceRefs.length > 0 ? <p className="muted small">Source records: {surface.sourceRefs.length}</p> : null}
    </section>
  );
}
